/**
 * Utilidades de renderização do painel administrativo (DOM puro, fora do AngularJS).
 * Todo dado vindo da API passa por escaparHtml: nome de dispositivo e de tenant são digitados pelo cliente.
 */
export function escaparHtml(valor: any): string {
    if (valor === null || valor === undefined) {
        return '';
    }

    return String(valor)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

/** Datas da API vêm como "2026-10-16 17:36:24.127+00" ou "2026-10-16 17:36:24". */
export function paraData(valor: string | null): Date | null {
    if (!valor) {
        return null;
    }

    const normalizado = String(valor)
        .replace(' ', 'T')
        .replace(/\.\d+/, '')
        .replace(/([+-]\d{2})$/, '$1:00');
    const data = new Date(normalizado);

    return isNaN(data.getTime()) ? null : data;
}

export function formatarDataHora(valor: string | null): string {
    const data = paraData(valor);
    if (!data) {
        return '—';
    }

    const dois = (n: number) => (n < 10 ? '0' : '') + n;

    return `${dois(data.getDate())}/${dois(data.getMonth() + 1)}/${data.getFullYear()} `
        + `${dois(data.getHours())}:${dois(data.getMinutes())}`;
}

export function diasDesde(valor: string | null): number | null {
    const data = paraData(valor);
    if (!data) {
        return null;
    }

    return Math.floor((Date.now() - data.getTime()) / 86400000);
}

export function badge(texto: string, tom: 'ok' | 'alerta' | 'erro' | 'neutro'): string {
    return `<span class="pa-badge pa-badge--${tom}">${escaparHtml(texto)}</span>`;
}

export function mensagem(texto: string, tom: 'erro' | 'sucesso' | 'info' = 'info'): string {
    return `<div class="pa-msg pa-msg--${tom}">${escaparHtml(texto)}</div>`;
}

export function contemTexto(busca: string, ...campos: any[]): boolean {
    if (!busca) {
        return true;
    }

    const alvo = busca.toLowerCase();

    return campos.some(campo => campo !== null && campo !== undefined && String(campo).toLowerCase().indexOf(alvo) > -1);
}

export type Direcao = 'asc' | 'desc';

export interface Ordenacao {
    coluna: string;
    direcao: Direcao;
}

export interface ColunaOrdenavel<T> {
    chave: string;
    rotulo: string;
    valor: (linha: T) => any;
    classe?: string;
}

/**
 * Cabeçalho clicável: o módulo escuta data-acao="ordenar" e alterna asc → desc → asc.
 */
export function cabecalhoOrdenavel<T>(tabela: string, colunas: ColunaOrdenavel<T>[], ordem: Ordenacao | null, extras = ''): string {
    return `<tr>${colunas.map(coluna => {
        const ativa = ordem && ordem.coluna === coluna.chave;
        const icone = !ativa ? 'fa-sort' : ordem.direcao === 'asc' ? 'fa-sort-up' : 'fa-sort-down';

        return `<th class="pa-ordenavel ${ativa ? 'pa-ordenavel--ativa' : ''} ${coluna.classe || ''}"
            data-acao="ordenar" data-tabela="${escaparHtml(tabela)}" data-coluna="${escaparHtml(coluna.chave)}"
            title="Ordenar por ${escaparHtml(coluna.rotulo)}">${escaparHtml(coluna.rotulo)} <i class="fas ${icone}"></i></th>`;
    }).join('')}${extras}</tr>`;
}

/** Ordena uma cópia; número e data comparam como valor, texto em pt-BR, vazio sempre por último. */
export function ordenarLinhas<T>(linhas: T[], colunas: ColunaOrdenavel<T>[], ordem: Ordenacao | null): T[] {
    const coluna = ordem ? colunas.find(c => c.chave === ordem.coluna) : null;
    if (!coluna) {
        return linhas.slice();
    }

    const sinal = ordem.direcao === 'asc' ? 1 : -1;

    return linhas.slice().sort((a, b) => {
        const va = coluna.valor(a);
        const vb = coluna.valor(b);
        const vazioA = va === null || va === undefined || va === '';
        const vazioB = vb === null || vb === undefined || vb === '';
        if (vazioA || vazioB) {
            return vazioA === vazioB ? 0 : vazioA ? 1 : -1;
        }
        if (typeof va === 'number' && typeof vb === 'number') {
            return (va - vb) * sinal;
        }
        if (typeof va === 'boolean' && typeof vb === 'boolean') {
            return (Number(va) - Number(vb)) * sinal;
        }

        return String(va).localeCompare(String(vb), 'pt-BR', { numeric: true, sensitivity: 'base' }) * sinal;
    });
}

export function alternarOrdenacao(atual: Ordenacao | null, coluna: string): Ordenacao {
    if (atual && atual.coluna === coluna) {
        return { coluna, direcao: atual.direcao === 'asc' ? 'desc' : 'asc' };
    }

    return { coluna, direcao: 'asc' };
}
